import { FC } from "react";
import {
  FormHelperText as MuiFormHelperText,
  InputLabel as MuiInputLabel,
  TextField as MuiTextField,
} from "@mui/material";
import styles from "./base-text-input.styles.module.scss";
import classNames from "classnames";
import { BaseTextInputProps } from "./base-text-input.types";

export const BaseTextInputComponent: FC<BaseTextInputProps> = ({
  ariaLabel,
  allowCharLimitOverflow = false,
  allowResize = false,
  autoFocus = false,
  charLimit = 0,
  endIcon,
  helperText = "",
  inputProps = {},
  InputProps = {},
  isDisabled = false,
  isError = false,
  isMultiline = false,
  label = "",
  maxRows,
  minRows = 1,
  onChange = () => {},
  onFocus = () => {},
  onBlur = () => {},
  onKeyDown = () => {},
  placeholder = "",
  rootClassName,
  showCharLimit = false,
  showOptionalLabel = false,
  size = "default",
  startIcon,
  value,
}) => {
  const displayValue = !allowCharLimitOverflow && charLimit > 0 ? value?.slice(0, charLimit) : value;

  return (
    <div
      className={classNames(
        styles.container,
        rootClassName
      )}
    >
      {label && (
        <div className={styles.labelContainer}>
          <MuiInputLabel classes={{ root: styles.labelRoot }}>{label}</MuiInputLabel>
          {showOptionalLabel && (
            <MuiInputLabel classes={{ root: styles.labelOptional }}>
              {"Optional"}
            </MuiInputLabel>
          )}
        </div>
      )}
      <MuiTextField
        classes={{
          root: styles.textFieldRoot
        }}
        inputProps={{
          ...inputProps,
          "aria-label": ariaLabel,
          maxLength: !allowCharLimitOverflow && charLimit > 0 ? charLimit : undefined,
          style: {
            minHeight: "24px",
            maxHeight: maxRows && {
              ["small"]: `${(maxRows * 24) + 12}px`,
              ["default"]: `${(maxRows * 24) + 20}px`,
            }[size],
            resize: isMultiline && allowResize ? "vertical" : "none"
          },
        }}
        InputProps={{
          ...InputProps,
          classes: {
            root: classNames(
              styles.inputRoot,
              size === "small" && styles.sizeSmall,
            ),
            input: styles.input,
            notchedOutline: isError ? styles.notchedOutlineError : styles.notchedOutline,
            focused: styles.inputFocus,
            disabled: styles.inputDisabled,
            error: styles.notchedOutlineError,
            adornedStart: classNames(startIcon && styles.adornedStart),
            adornedEnd: classNames(endIcon && styles.adornedEnd),
          },
          startAdornment: startIcon && <div className={styles.iconWrapper}>{startIcon}</div>,
          endAdornment: endIcon && <div className={styles.iconWrapper}>{endIcon}</div>,
        }}
        variant={"outlined"}
        minRows={minRows}
        maxRows={maxRows}
        label={undefined}
        error={isError}
        fullWidth={true}
        placeholder={placeholder}
        disabled={isDisabled}
        multiline={isMultiline}
        value={displayValue}
        onChange={onChange}
        onFocus={onFocus}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        autoFocus={autoFocus}
      />
      <div className={styles.helperRoot}>
        <MuiFormHelperText
          classes={{
            root: classNames(
              styles.helperText,
              isError && styles.helperTextError,
            )
          }}
        >
          {helperText}
        </MuiFormHelperText>
        {showCharLimit && charLimit > 0 && (
          <MuiFormHelperText
            classes={{
              root: classNames(
                styles.helperText,
                isError && styles.helperTextError,
              )
            }}
          >
            {`${displayValue?.length || 0}/${charLimit}`}
          </MuiFormHelperText>
        )}
      </div>
    </div>
  );
};
